import React, { useEffect } from 'react';
import { Flex } from '@chakra-ui/react';
import { StorefrontPreview } from '@salesforce/commerce-sdk-react/components';
import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext';
import { Helmet } from 'react-helmet';
import { IntlProvider } from 'react-intl';
import { useAddPreviewParamIfNeeded } from '../../bff-api/customers/useAddPreviewParamIfNeeded';
import { useSSRPreviewMode } from '../../bff-api/customers/useSSRPreviewMode';
import { DEFAULT_LOCALE } from '../../constants';
import { useActOnPreviewContextChange } from '../../hooks/useActOnPreviewContextChange';
import { SLTConfigProvider } from '../../slt/config/SLTConfigProvider';
import { useSLTAppConfigs } from '../../slt/config/useConfigs';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { sfccUrl } from '../../utils/sitegenesis';
import { isServer } from '../../utils/utils';
import { AppHead } from '../_app/AppHead';
import { EnhancedError } from './EnhancedError';

// <Error> is rendered when:
//
// 1. A user requests a page that is not routable from `app/routes.jsx`.
// 2. A routed component throws an error in `getProps()`.
// 3. A routed component throws an error in `render()`.
//
// It must not throw an error. Keep it as simple as possible.

const Error = (props) => {
    const isSSRPreviewMode = useSSRPreviewMode();
    const authContext = useAuthContext();
    const getTokenWhenReady = async () => {
        if (!isServer) await window.sfccSessionCreationPromise;
        return authContext.ready().then((data) => data.access_token);
    };
    useEffect(() => {
        authContext.ready().finally(() => {
            if (!isServer) {
                window.completePwaAuthFlow();
            }
        });
    }, [authContext]);
    useAddPreviewParamIfNeeded();
    useActOnPreviewContextChange();
    const sltServerConfig = useSLTAppConfigs();
    const gtmId = sltServerConfig?.customPreferences?.[CustomPreference.GOOGLE_TAG_MANAGER_ID];
    const applePayScanToPayEnabled =
        sltServerConfig?.customPreferences?.[CustomPreference.APPLE_PAY_SCAN_TO_PAY_ENABLED];
    return (
        <SLTConfigProvider data={sltServerConfig}>
            <Helmet>
                <script type="text/javascript">
                    {`
                        /* If browser back button was used, flush cache */
                        (function () {
                            if(!window.localStorage.getItem('is-refresh')) {
                                window.onpageshow = event => {
                                if (event.persisted) {
                                    window.localStorage.setItem('is-refresh', true);
                                    window.location.reload();
                                }
                                }
                            } else {
                                window.localStorage.removeItem('is-refresh');
                            }
                        })();
                    `}
                </script>

                {/**
                 * This is a script to make sure the sessions (SG / PWA) are in sync
                 * Without this, the PWA application will not be able to see the auth token in the cookies
                 * and will try to generate a new one based on the refresh token every time.
                 */}
                <script>
                    {`
                        (function(w){
                            w.completePwaAuthFlow = null;
                            w.pwaAuthFlowPromise = new Promise((res, _) => {
                                w.completePwaAuthFlow = res;
                            });
                            var isLocationOnMobifyHost = w.location?.hostname?.endsWith('.mobify-storefront.com');
                            var prefix = isLocationOnMobifyHost ? '/mobify/proxy/ocapi' : '';
                            w.sfccSessionCreationPromise =
                                fetch(prefix+'${sfccUrl('PwaUtils-EnsureSiteGenesisSession', [
                                    { t: '' },
                                ])}'+Date.now())
                                    .then((r)=>{if (!r.ok){throw new Error('HTTP Error calling PwaUtils-EnsureSiteGenesisSession');}return r;})
                                    .then((r) => r.text())
                                    .catch((e) => {console.warn('There was an error creating the SG session', e);});
                        })(window);
                    `}
                </script>
                <script type="text/javascript">
                    {`
                        (function(){
                            /* This is to avoid Safari from sending basic Auth */
                            /* https://stackoverflow.com/questions/36789514/how-to-prevent-override-authorization-header-in-safari */
                            var savedFetch = window.fetch;
                            window.fetch = function() {
                                var args = [].slice.call(arguments, 0);
                                if (args.length > 0 && /.*\\/commerce-api\\/.*/.test(args[0])) {
                                    var options = Object.assign({}, args[1] || {});
                                    options.headers = Object.assign({}, options.headers || {});
                                    options.headers['x-slt-rewriteauth'] = options.headers.Authorization || options.headers.authorization || '';
                                    args[1] = options;
                                }
                                return savedFetch.apply(this, args);
                            }
                        })();
                    `}
                </script>
                {applePayScanToPayEnabled ? (
                    <script src="https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js" />
                ) : null}
            </Helmet>
            <AppHead />
            {gtmId && !isSSRPreviewMode ? (
                <noscript>
                    <iframe
                        title="GTM iframe"
                        src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
                        height="0"
                        width="0"
                        style={{ display: 'none', visibility: 'hidden' }}
                    />
                </noscript>
            ) : null}
            <Flex id="sf-app" flex={1} direction="column" minWidth="375px">
                <StorefrontPreview getToken={getTokenWhenReady}>
                    <IntlProvider locale={DEFAULT_LOCALE} defaultLocale={DEFAULT_LOCALE}>
                        <EnhancedError error={props} />
                    </IntlProvider>
                </StorefrontPreview>
            </Flex>
        </SLTConfigProvider>
    );
};

export default Error;
